import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star, Sparkles, BadgeCheck, BarChart3, MessageSquare, Image, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import axios from "axios";
import { API, useAuth } from "../App";
import { startCheckout } from "../lib/checkout";
import { SHOW_PAID_PLANS } from "../lib/featureFlags";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (planId === "plan_free") {
      navigate("/dashboard");
      return;
    }
    setStarting(planId);
    await startCheckout(navigate, { kind: "subscription", plan_id: planId });
    setStarting(null);
  };

  const defaultPlans = [
    {
      plan_id: "plan_free",
      name: "Free",
      price: 0,
      currency: "ZAR",
      duration_months: 12,
      features: [
        "Basic profile",
        "5 portfolio items",
        "Contact details visible (Email, WhatsApp)",
        "Website backlink",
        "Appear in search results",
      ],
      portfolio_limit: 5,
      is_featured: false,
    },
    {
      plan_id: "plan_pro",
      name: "Pro",
      price: 199,
      currency: "ZAR",
      duration_months: 1,
      features: [
        "Everything in Free",
        "Verified badge",
        "20 portfolio items",
        "Direct messaging through platform",
        "Priority in search",
        "Profile analytics",
        "Website backlink (do-follow)",
      ],
      portfolio_limit: 20,
      is_featured: false,
      popular: true,
    },
    {
      plan_id: "plan_premium",
      name: "Premium",
      price: 499,
      currency: "ZAR",
      duration_months: 1,
      features: [
        "Everything in Pro",
        "Featured profile badge",
        "Unlimited portfolio",
        "Top search placement",
        "Advanced analytics",
        "Priority support",
        "Highlighted in categories",
        "Premium website backlink",
      ],
      portfolio_limit: 100,
      is_featured: true,
    },
  ];

  const allPlans = plans.length > 0 ? plans : defaultPlans;
  const displayPlans = SHOW_PAID_PLANS ? allPlans : allPlans.filter((p) => p.price === 0);
  const gridCols = SHOW_PAID_PLANS ? "md:grid-cols-3" : "max-w-md mx-auto";

  const getIcon = (feature) => {
    if (feature.includes("portfolio")) return <Image className="h-4 w-4 text-orange-500" />;
    if (feature.includes("analytics")) return <BarChart3 className="h-4 w-4 text-orange-500" />;
    if (feature.includes("messaging")) return <MessageSquare className="h-4 w-4 text-orange-500" />;
    if (feature.includes("Verified")) return <BadgeCheck className="h-4 w-4 text-teal-500" />;
    if (feature.includes("Featured")) return <Sparkles className="h-4 w-4 text-orange-500" />;
    if (feature.includes("Priority")) return <Zap className="h-4 w-4 text-orange-500" />;
    return <Check className="h-4 w-4 text-orange-500" />;
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="pricing-page">
      <SEO 
        url="/pricing"
        title="Pricing Plans"
        description={SHOW_PAID_PLANS ? "Choose your plan and get discovered by more clients. Free, Pro (R199/mo), and Premium (R499/mo) plans for creative professionals in Cape Town." : "Create your free creative profile and get discovered by clients in Cape Town."}
        keywords={["creative directory pricing", "premium profile", "featured profile", "videographer subscription"]}
      />
      
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-title uppercase">
            {SHOW_PAID_PLANS ? <>CHOOSE YOUR <span className="text-orange-500">PLAN</span></> : <>JOIN FOR <span className="text-orange-500">FREE</span></>}
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl mx-auto">
            {SHOW_PAID_PLANS ? "Get discovered by more clients, showcase more work, and grow your creative career" : "Create your profile, showcase your work and get discovered by clients in Cape Town"}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(SHOW_PAID_PLANS ? 3 : 1)].map((_, i) => (
              <div key={i} className="h-96 bg-zinc-900 skeleton" />
            ))}
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-8 ${gridCols}`}>
            {displayPlans.map((plan, index) => (
              <div
                key={plan.plan_id}
                data-testid={`plan-card-${plan.plan_id}`}
                className={`relative bg-zinc-900 border ${
                  plan.popular || plan.is_featured
                    ? "border-orange-500"
                    : "border-zinc-800"
                } p-8 ${plan.popular ? "tracing-beam" : ""}`}
              >
                {(plan.popular || plan.is_featured) && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-orange-600 text-white text-xs font-mono uppercase tracking-wider">
                      {plan.is_featured ? "Best Value" : "Most Popular"}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="font-heading text-2xl text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-5xl text-white">
                      {plan.price === 0 ? "R0" : `R${plan.price}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-zinc-500">/month</span>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300">
                      {getIcon(feature)}
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.plan_id)}
                  className={`w-full ${
                    plan.price === 0
                      ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
                      : plan.is_featured
                      ? "bg-orange-600 hover:bg-orange-500"
                      : "bg-zinc-800 hover:bg-orange-600"
                  }`}
                  disabled={starting === plan.plan_id}
                  data-testid={`subscribe-${plan.plan_id}`}
                >
                  {plan.price === 0 ? "Get Started" : starting === plan.plan_id ? "Starting checkout…" : "Subscribe"}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Cancellation Notice */}
        {SHOW_PAID_PLANS && <div className="mt-12 bg-zinc-900/50 border border-zinc-800 p-6 text-center max-w-3xl mx-auto">
          <p className="text-zinc-400 text-sm">
            <span className="text-orange-500 font-medium">Monthly billing, cancel anytime:</span>{" "}
            Your card is billed securely every 30 days by Peach Payments. Cancel from your dashboard at any time —
            you won't be charged again and your benefits stay active until the end of your current billing period.
          </p>
        </div>}

        {/* FAQ Section */}
        {SHOW_PAID_PLANS && <div className="mt-20">
          <h2 className="font-heading text-2xl text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "Can I cancel my subscription anytime?",
                a: "Yes. Cancel from the Billing tab in your dashboard. You won't be charged again and your benefits stay active until the end of your billing period.",
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept Visa, Mastercard and Amex credit/debit cards via Peach Payments, South Africa's secure payment gateway.",
              },
              {
                q: "Can I upgrade or downgrade my plan?",
                a: "Absolutely! You can change your plan at any time. Changes take effect immediately.",
              },
              {
                q: "Do you offer refunds?",
                a: "We offer a 14-day money-back guarantee if you're not satisfied with your subscription.",
              },
            ].map((faq, index) => (
              <div key={index} className="bg-zinc-900 border border-zinc-800 p-6">
                <h4 className="font-medium text-white mb-2">{faq.q}</h4>
                <p className="text-zinc-500 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>}

        {/* Advertise Section */}
        <div id="advertise" className="mt-20 bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-800 p-8 md:p-12 text-center">
          <h2 className="font-heading text-2xl text-white mb-4">
            Advertise on Videography Cape Town
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-6">
            Reach thousands of creative professionals and clients. 
            Place banner ads on our homepage, category pages, and search results.
          </p>
          <Button
            onClick={() => navigate(user?.user_type === "advertiser" ? "/advertiser" : "/advertise")}
            data-testid="pricing-advertise-btn"
            className="bg-orange-600 hover:bg-orange-500"
          >
            Contact for Advertising
          </Button>
        </div>
      </div>
    </div>
  );
}
